import React from "react";
import { Link } from "react-router-dom";
import logo from "assets/images/logo.png";

// Page publique — exigée par Google (écran de consentement OAuth / vérification
// Google Ads API) et par les autres plateformes publicitaires connectées.
const LAST_UPDATED = "28 juillet 2026";
const CONTACT_EMAIL = "contact@onebotads.net";

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-bold text-[#221C51] mb-2">{title}</h2>
    <div className="text-sm text-gray-600 leading-relaxed space-y-3">{children}</div>
  </section>
);

const TermsOfService = () => (
  <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
    <div className="border-b border-gray-100 bg-white">
      <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2">
          <img src={logo} alt="OneBotAds" className="h-7" />
        </Link>
        <Link to="/" className="text-sm text-gray-500 hover:text-[#221C51]">← Retour à l'accueil</Link>
      </div>
    </div>

    <div className="max-w-4xl mx-auto px-6 py-14">
      <h1 className="text-3xl md:text-4xl font-bold text-[#221C51] mb-2">Conditions d'utilisation</h1>
      <p className="text-sm text-gray-400 mb-10">Dernière mise à jour : {LAST_UPDATED}</p>

      <Section title="1. Acceptation des conditions">
        <p>
          En créant un compte ou en utilisant OneBotAds (le "Service"), vous acceptez les présentes
          Conditions d'utilisation ainsi que notre{" "}
          <Link to="/privacy-policy" className="text-[#221C51] underline">Politique de confidentialité</Link>.
          Si vous n'acceptez pas ces conditions, n'utilisez pas le Service.
        </p>
      </Section>

      <Section title="2. Description du service">
        <p>
          OneBotAds est une plateforme permettant de créer, lancer, suivre et automatiser des campagnes
          publicitaires sur Meta, Google Ads, TikTok, Snapchat et LinkedIn, de générer des créatifs
          publicitaires par IA (image, vidéo, voix off), et de connecter des boutiques Shopify. Le Service
          agit en votre nom sur ces plateformes tierces, via les autorisations (OAuth) que vous accordez.
        </p>
      </Section>

      <Section title="3. Compte utilisateur">
        <ul className="list-disc pl-5 space-y-1">
          <li>Vous devez fournir des informations exactes lors de la création de votre compte.</li>
          <li>Vous êtes responsable de la confidentialité de vos identifiants et de toute activité effectuée depuis votre compte.</li>
          <li>Vous devez avoir 18 ans ou plus pour utiliser le Service.</li>
          <li>Un seul compte par personne, sauf accord contraire.</li>
        </ul>
      </Section>

      <Section title="4. Comptes publicitaires tiers connectés">
        <p>
          En connectant un compte Meta, Google Ads, TikTok, Snapchat ou LinkedIn, vous autorisez OneBotAds
          à agir en votre nom (lecture et création de campagnes, groupes d'annonces, annonces, pixels)
          dans la limite des actions que vous initiez dans l'application. Vous restez seul responsable du
          respect des conditions d'utilisation et politiques publicitaires de chacune de ces plateformes
          (contenu autorisé, ciblage, budgets, facturation directe par la régie). OneBotAds n'est ni
          affilié à, ni approuvé par Meta, Google, TikTok, Snapchat ou LinkedIn.
        </p>
      </Section>

      <Section title="5. Abonnement, crédits et facturation">
        <ul className="list-disc pl-5 space-y-1">
          <li>La création de campagnes, le suivi et l'A/B testing sont inclus gratuitement dans tous les paliers.</li>
          <li>La génération de créatifs IA (image/vidéo/voix) consomme des crédits, selon le barème affiché dans l'application au moment de la génération.</li>
          <li>Les abonnements payants sont facturés d'avance, selon la périodicité choisie, et se renouvellent automatiquement sauf résiliation avant la date de renouvellement.</li>
          <li>Les crédits non consommés en fin de période ne sont pas remboursables, sauf disposition légale contraire applicable dans votre juridiction.</li>
          <li>Les dépenses publicitaires elles-mêmes (budgets de campagne) sont facturées directement par chaque régie sur le moyen de paiement que vous y avez enregistré — OneBotAds ne facture jamais votre budget publicitaire.</li>
        </ul>
      </Section>

      <Section title="6. Utilisation acceptable">
        <p>Vous vous engagez à ne pas utiliser le Service pour :</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Créer ou diffuser des publicités illégales, trompeuses, ou enfreignant les politiques publicitaires des plateformes connectées.</li>
          <li>Générer du contenu IA illégal, diffamatoire, portant atteinte à des droits de tiers (image, marque, droit d'auteur), ou à caractère haineux/violent/sexuel explicite.</li>
          <li>Tenter d'accéder sans autorisation à des comptes, données ou systèmes tiers.</li>
          <li>Perturber, surcharger ou compromettre la sécurité du Service.</li>
          <li>Revendre ou redistribuer l'accès au Service sans autorisation écrite.</li>
        </ul>
        <p>Tout manquement peut entraîner la suspension ou la résiliation immédiate de votre compte.</p>
      </Section>

      <Section title="7. Contenu généré par IA">
        <p>
          Les images, vidéos et voix générées via le Service utilisent des modèles d'intelligence
          artificielle tiers (Google Imagen/Veo/Gemini, ByteDance Seedance, Higgsfield, Replicate, Hugging
          Face). Vous êtes seul responsable de vérifier que le contenu généré et son usage publicitaire
          respectent la loi applicable et les politiques des plateformes de diffusion. OneBotAds ne garantit
          pas l'exactitude, la qualité ou l'absence d'erreur du contenu généré par IA.
        </p>
      </Section>

      <Section title="8. Propriété intellectuelle">
        <p>
          Le Service, sa marque et son code restent la propriété d'OneBotAds. Vous conservez tous les droits
          sur vos propres contenus (produits, marques, textes) importés dans l'application, ainsi que sur
          les créatifs générés à votre demande, dans la limite des droits accordés par les fournisseurs
          d'IA tiers sur le contenu qu'ils génèrent.
        </p>
      </Section>

      <Section title="9. Limitation de responsabilité">
        <p>
          Le Service est fourni "en l'état". Dans la limite permise par la loi, OneBotAds ne pourra être
          tenu responsable des pertes indirectes, pertes de profit, ou dommages résultant de l'utilisation
          du Service, y compris des décisions publicitaires prises via l'application, des interruptions de
          service des plateformes tierces connectées, ou des résultats de campagnes publicitaires.
        </p>
      </Section>

      <Section title="10. Résiliation">
        <p>
          Vous pouvez résilier votre compte à tout moment depuis les paramètres. Nous pouvons suspendre ou
          résilier votre accès en cas de violation de ces conditions, avec notification préalable sauf
          urgence (fraude, sécurité).
        </p>
      </Section>

      <Section title="11. Droit applicable">
        <p>Les présentes conditions sont régies par le droit applicable au lieu d'établissement d'OneBotAds, sans préjudice des droits impératifs dont vous bénéficiez en tant que consommateur dans votre pays de résidence.</p>
      </Section>

      <Section title="12. Modifications">
        <p>Nous pouvons modifier ces conditions ; les changements substantiels vous seront notifiés. La poursuite de l'utilisation du Service après notification vaut acceptation des nouvelles conditions.</p>
      </Section>

      <Section title="13. Contact">
        <p>
          Pour toute question relative à ces conditions : {" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#221C51] underline">{CONTACT_EMAIL}</a>.
        </p>
      </Section>
    </div>
  </div>
);

export default TermsOfService;
